import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  AlertTriangle,
  DollarSign,
  Percent,
  ArrowUpDown
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const PriceChanges = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priceChanges, setPriceChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  
  // Estados para el formulario
  const [formData, setFormData] = useState({
    tipo_cambio: 'individual',
    metodo_cambio: 'porcentaje',
    valor_cambio: 0,
    producto_id: '',
    categoria_id: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchPriceChanges();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.filter(cat => cat.activa));
    } catch (error) {
      toast.error('Error al cargar categorías');
    }
  };

  const fetchPriceChanges = async () => {
    try {
      const response = await axios.get(`${API}/price-changes`);
      setPriceChanges(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  const calculatePreview = () => {
    let productosAfectados = [];

    // Determinar productos según tipo de cambio
    if (formData.tipo_cambio === 'individual') {
      if (!formData.producto_id) {
        toast.error('Selecciona un producto');
        return;
      }
      const producto = products.find(p => p.id === formData.producto_id);
      if (producto) productosAfectados = [producto];
    } else if (formData.tipo_cambio === 'categoria') {
      if (!formData.categoria_id) {
        toast.error('Selecciona una categoría');
        return;
      }
      productosAfectados = products.filter(p => p.categoria === formData.categoria_id);
    } else if (formData.tipo_cambio === 'masivo') {
      productosAfectados = products;
    }

    if (productosAfectados.length === 0) {
      toast.error('No hay productos para aplicar el cambio');
      return;
    }

    // Calcular nuevos precios
    const preview = productosAfectados.map(producto => {
      const precioAnterior = producto.precio_venta;
      let precioNuevo = precioAnterior;

      if (formData.metodo_cambio === 'porcentaje') {
        precioNuevo = precioAnterior * (1 + parseFloat(formData.valor_cambio) / 100);
      } else if (formData.metodo_cambio === 'valor_fijo') {
        precioNuevo = parseFloat(formData.valor_cambio);
      } else if (formData.metodo_cambio === 'incremento') {
        precioNuevo = precioAnterior + parseFloat(formData.valor_cambio);
      }

      precioNuevo = Math.max(0, parseFloat(precioNuevo.toFixed(2)));
      const diferencia = precioNuevo - precioAnterior;
      const porcentajeCambio = precioAnterior > 0 ? (diferencia / precioAnterior * 100) : 0;

      return {
        ...producto,
        precio_anterior: precioAnterior,
        precio_nuevo: precioNuevo,
        diferencia: diferencia,
        porcentaje_cambio: porcentajeCambio
      };
    });

    setPreviewData(preview);
    setShowPreviewDialog(true);
  };

  const handleApplyChanges = async () => {
    if (!formData.valor_cambio || formData.valor_cambio === 0) {
      toast.error('Ingresa un valor de cambio válido');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        tipo_cambio: formData.tipo_cambio,
        metodo_cambio: formData.metodo_cambio,
        valor_cambio: parseFloat(formData.valor_cambio),
        producto_id: formData.producto_id || null,
        categoria_id: formData.categoria_id || null
      };

      await axios.post(`${API}/price-changes`, payload);
      
      toast.success('Cambio de precios aplicado exitosamente');
      setShowPreviewDialog(false);
      resetForm();
      fetchProducts();
      fetchPriceChanges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar cambio');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_cambio: 'individual',
      metodo_cambio: 'porcentaje',
      valor_cambio: 0,
      producto_id: '',
      categoria_id: ''
    });
    setPreviewData([]);
  };

  const viewChangeDetail = async (changeId) => {
    try {
      const response = await axios.get(`${API}/price-changes/${changeId}`);
      setSelectedChange(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar el detalle');
    }
  };

  const getTipoCambioLabel = (tipo) => {
    const labels = {
      individual: 'Individual',
      categoria: 'Por Categoría',
      masivo: 'Masivo'
    };
    return labels[tipo] || tipo;
  };

  const getMetodoLabel = (metodo) => {
    const labels = {
      porcentaje: 'Porcentaje',
      valor_fijo: 'Valor Fijo',
      incremento: 'Incremento/Decremento'
    };
    return labels[metodo] || metodo;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Cambio de Precios</h1>
        <p className="text-slate-600">Actualización individual, por categoría o masiva de precios de venta</p>
      </div>

      {/* Formulario de cambio */}
      <Card>
        <CardHeader>
          <CardTitle>Aplicar Cambio de Precios</CardTitle>
          <CardDescription>Selecciona el tipo de cambio y configura los parámetros</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={formData.tipo_cambio} onValueChange={(value) => setFormData({ ...formData, tipo_cambio: value, producto_id: '', categoria_id: '' })}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="individual">Individual</TabsTrigger>
              <TabsTrigger value="categoria">Por Categoría</TabsTrigger>
              <TabsTrigger value="masivo">Masivo</TabsTrigger>
            </TabsList>

            {/* Tab Individual */}
            <TabsContent value="individual" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Producto *</Label>
                  <Select value={formData.producto_id} onValueChange={(value) => setFormData({ ...formData, producto_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.nombre} - {formatCurrency(product.precio_venta)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Tab Por Categoría */}
            <TabsContent value="categoria" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Categoría *</Label>
                  <Select value={formData.categoria_id} onValueChange={(value) => setFormData({ ...formData, categoria_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Tab Masivo */}
            <TabsContent value="masivo" className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-600 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-amber-800">Cambio masivo</p>
                    <p className="text-sm text-amber-700">Este cambio se aplicará a TODOS los productos del sistema ({products.length} productos)</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Método de cambio - Común para todos los tabs */}
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Método de Cambio *</Label>
                <Select value={formData.metodo_cambio} onValueChange={(value) => setFormData({ ...formData, metodo_cambio: value, valor_cambio: 0 })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="porcentaje">
                      <div className="flex items-center gap-2">
                        <Percent size={16} />
                        <span>Porcentaje</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="valor_fijo">
                      <div className="flex items-center gap-2">
                        <DollarSign size={16} />
                        <span>Valor Fijo</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="incremento">
                      <div className="flex items-center gap-2">
                        <ArrowUpDown size={16} />
                        <span>Incremento/Decremento</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>
                  {formData.metodo_cambio === 'porcentaje' && 'Porcentaje (%)'}
                  {formData.metodo_cambio === 'valor_fijo' && 'Nuevo Precio (RD$)'}
                  {formData.metodo_cambio === 'incremento' && 'Cambio (RD$)'}
                  {' *'}
                </Label>
                <Input
                  type="number"
                  step={formData.metodo_cambio === 'porcentaje' ? '1' : '0.01'}
                  value={formData.valor_cambio}
                  onChange={(e) => setFormData({ ...formData, valor_cambio: e.target.value })}
                  placeholder={
                    formData.metodo_cambio === 'porcentaje' ? 'Ej: 10 para +10%, -5 para -5%' :
                    formData.metodo_cambio === 'valor_fijo' ? 'Ej: 150.00' :
                    'Ej: 20 para +RD$20, -10 para -RD$10'
                  }
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.metodo_cambio === 'porcentaje' && 'Valores positivos aumentan, negativos disminuyen'}
                  {formData.metodo_cambio === 'valor_fijo' && 'Todos los productos tendrán este precio'}
                  {formData.metodo_cambio === 'incremento' && 'Valores positivos suman, negativos restan'}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculatePreview} className="bg-blue-600 hover:bg-blue-700">
                <Eye className="mr-2" size={18} />
                Vista Previa
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historial de cambios */}
      <Card>
        <CardHeader>
          <CardTitle>Historial de Cambios</CardTitle>
          <CardDescription>Registro de todos los cambios de precios realizados</CardDescription>
        </CardHeader>
        <CardContent>
          {priceChanges.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <DollarSign size={48} className="mx-auto mb-2 opacity-50" />
              <p>No hay cambios de precios registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Fecha</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Método</th>
                    <th className="text-left p-3">Productos Afectados</th>
                    <th className="text-left p-3">Usuario</th>
                    <th className="text-left p-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {priceChanges.map((change) => (
                    <tr key={change.id} className="border-t hover:bg-slate-50">
                      <td className="p-3">{new Date(change.fecha).toLocaleString()}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          {getTipoCambioLabel(change.tipo_cambio)}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm">{getMetodoLabel(change.metodo_cambio)}</span>
                      </td>
                      <td className="p-3 font-semibold">{change.total_productos_afectados}</td>
                      <td className="p-3">{change.usuario_nombre}</td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewChangeDetail(change.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Eye size={16} className="mr-1" />
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Vista Previa */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">Vista Previa de Cambios</DialogTitle>
            <DialogDescription>
              Revisa los cambios antes de aplicarlos. Total de productos: {previewData.length}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-3">Producto</th>
                    <th className="text-right p-3">Precio Anterior</th>
                    <th className="text-right p-3">Precio Nuevo</th>
                    <th className="text-right p-3">Diferencia</th>
                    <th className="text-right p-3">% Cambio</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-3">
                        <p className="font-medium">{item.nombre}</p>
                        <p className="text-xs text-slate-600">{item.codigo_interno}</p>
                      </td>
                      <td className="p-3 text-right">{formatCurrency(item.precio_anterior)}</td>
                      <td className="p-3 text-right font-semibold text-blue-600">
                        {formatCurrency(item.precio_nuevo)}
                      </td>
                      <td className="p-3 text-right">
                        <span className={`inline-flex items-center gap-1 font-semibold ${
                          item.diferencia > 0 ? 'text-green-600' : item.diferencia < 0 ? 'text-red-600' : 'text-slate-600'
                        }`}>
                          {item.diferencia > 0 && <TrendingUp size={14} />}
                          {item.diferencia < 0 && <TrendingDown size={14} />}
                          {formatCurrency(Math.abs(item.diferencia))}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className={`font-semibold ${
                          item.porcentaje_cambio > 0 ? 'text-green-600' : item.porcentaje_cambio < 0 ? 'text-red-600' : 'text-slate-600'
                        }`}>
                          {item.porcentaje_cambio > 0 ? '+' : ''}{item.porcentaje_cambio.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleApplyChanges} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
              <DollarSign className="mr-2" size={20} />
              {loading ? 'Aplicando...' : 'Confirmar y Aplicar Cambios'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle de Cambio */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedChange && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">Detalle de Cambio de Precios</DialogTitle>
                <DialogDescription>
                  {new Date(selectedChange.fecha).toLocaleString()} - {selectedChange.usuario_nombre}
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-4">
                {/* Información General */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Información del Cambio</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-sm text-slate-600">Tipo</Label>
                        <p className="font-semibold">{getTipoCambioLabel(selectedChange.tipo_cambio)}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600">Método</Label>
                        <p className="font-semibold">{getMetodoLabel(selectedChange.metodo_cambio)}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600">Valor Aplicado</Label>
                        <p className="font-semibold">
                          {selectedChange.metodo_cambio === 'porcentaje' && `${selectedChange.valor_cambio}%`}
                          {selectedChange.metodo_cambio === 'valor_fijo' && formatCurrency(selectedChange.valor_cambio)}
                          {selectedChange.metodo_cambio === 'incremento' && formatCurrency(selectedChange.valor_cambio)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600">Productos Afectados</Label>
                        <p className="font-semibold">{selectedChange.total_productos_afectados}</p>
                      </div>
                      {selectedChange.categoria_nombre && (
                        <div className="md:col-span-4">
                          <Label className="text-sm text-slate-600">Categoría</Label>
                          <p className="font-semibold">{selectedChange.categoria_nombre}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Productos Afectados */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Productos Modificados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3">Producto</th>
                            <th className="text-right p-3">Precio Anterior</th>
                            <th className="text-right p-3">Precio Nuevo</th>
                            <th className="text-right p-3">Diferencia</th>
                            <th className="text-right p-3">% Cambio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedChange.items.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="p-3">
                                <p className="font-medium">{item.producto_nombre}</p>
                                <p className="text-xs text-slate-600">{item.codigo_interno}</p>
                              </td>
                              <td className="p-3 text-right">{formatCurrency(item.precio_anterior)}</td>
                              <td className="p-3 text-right font-semibold">{formatCurrency(item.precio_nuevo)}</td>
                              <td className="p-3 text-right">
                                <span className={`inline-flex items-center gap-1 font-semibold ${
                                  item.diferencia > 0 ? 'text-green-600' : item.diferencia < 0 ? 'text-red-600' : 'text-slate-600'
                                }`}>
                                  {item.diferencia > 0 && <TrendingUp size={14} />}
                                  {item.diferencia < 0 && <TrendingDown size={14} />}
                                  {formatCurrency(Math.abs(item.diferencia))}
                                </span>
                              </td>
                              <td className="p-3 text-right">
                                <span className={`font-semibold ${
                                  item.porcentaje_cambio > 0 ? 'text-green-600' : item.porcentaje_cambio < 0 ? 'text-red-600' : 'text-slate-600'
                                }`}>
                                  {item.porcentaje_cambio > 0 ? '+' : ''}{item.porcentaje_cambio.toFixed(2)}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={() => setShowDetailDialog(false)} className="bg-slate-600 hover:bg-slate-700">
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceChanges;
